import React, { useState } from "react";
import * as Yup from "yup";
import { Breadcrumb } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { create } from "../../services/upload-files.service";

const NewReportForm = () => {
    const navigate = useNavigate();
    const [validationErrors, setValidationErrors] = useState({});
    const [statedata] = useState(useLocation().state);
    const [boxStatus, setBoxStatus] = useState(null);
    const { data } = useFetch('facilities');

    const goBack = () => {
        navigate(-1);
    };

    const validationSchema = Yup.object().shape({
        report_name: Yup.string().required("Report Name is Required"),
        from_date: Yup.date().nullable(), // Made optional
        to_date: Yup.date().nullable(), // Made optional
        facility_id: Yup.array().min(1, "Select at Least One Facility"),
    });

    const [formData, setFormData] = useState({
        report_name: '',
        from_date: '',
        to_date: '',
        sub_category_id: statedata.id ? statedata.id : "",
        facility_id: [],
        selectAll: false,
    });

    const handleInputChange = (e) => {
        const { id, type, checked } = e.target;

        setValidationErrors((prevErrors) => ({ ...prevErrors, [id]: null }));
        setFormData((prevData) => {
            if (type === 'checkbox') {
                if (id === 'selectAll') {
                    const idsArray = data.map(item => item.id);
                    const allFacilities = checked ? idsArray : [];
                    return {
                        ...prevData,
                        facility_id: allFacilities,
                        selectAll: checked,
                    };
                } else {
                    let updatedFacilityId;
                    if (checked) {
                        updatedFacilityId = [...prevData.facility_id, parseInt(id)];
                    } else {
                        updatedFacilityId = prevData.facility_id.filter((fac) => fac !== parseInt(id));
                    }

                    const isAllSelected = updatedFacilityId.length === data.length;

                    return {
                        ...prevData,
                        facility_id: isAllSelected ? [] : updatedFacilityId,
                        selectAll: isAllSelected,
                    };
                }
            } else {
                return {
                    ...prevData,
                    [id]: e.target.value,
                };
            }
        });
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();

        validationSchema
            .validate(formData, { abortEarly: false })
            .then(() => {
                setValidationErrors({});
                create(formData, 'reports/internal/CreateInternalReport').then((res) => {
                    if (res) {
                        setFormData({
                            report_name: '',
                            from_date: '',
                            to_date: '',
                            sub_category_id: statedata.id ? statedata.id : "",
                            facility_id: [],
                            selectAll: false,
                        });
                        setBoxStatus(null);
                        const route = statedata.subCategoryName === "Water & Wastewater Management" ? "/reportswaterdatadetails" : '/reportsdatadetails';
                        navigate(route, { state: { data: res, formData: formData } });
                    }
                });
            })
            .catch((errors) => {
                const formattedErrors = {};
                errors.inner.forEach((error) => {
                    formattedErrors[error.path] = error.message;
                });
                setValidationErrors(formattedErrors);
            });
    };

    // Function to handle date field focus
    const handleDateFieldFocus = (e) => {
        e.target.showPicker();
    };

    return (
        <>
            <style>
                {`
                .error-message {
                    color: red;
                    font-size: 0.875rem;
                    margin-top: 4px;
                }
                .date-input {
                    cursor: pointer;
                }
                `}
            </style>

            <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4">
                <div className="pt-3 pb-2 mb-3 border-bottom">
                    <h1 className="h2 float-start">
                        <Link onClick={goBack}>
                            <i className="fa-solid fa-arrow-left"></i>
                        </Link>
                    </h1>
                    <Breadcrumb className="breadcrumb-inner">
                        <Breadcrumb.Item>
                            <Link to="/reports">Reports</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item active>{statedata.displayName}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <div className="newreportform">
                    <h4>{statedata.displayName} Definition</h4>
                    <form onSubmit={handleFormSubmit}>
                        <div className="row mb-3">
                            <div className="col-auto formicon">
                                <i className="fa-solid fa-file-signature"></i>
                            </div>
                            <div className="col-sm-5 fromfield">
                                <label htmlFor="report_name">Report Name</label>
                                <span>Name Your Report (Example: GHG Report 2022)</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="report_name"
                                    value={formData.report_name}
                                    onChange={handleInputChange}
                                />
                                {validationErrors.report_name && (
                                    <div className="error-message">{validationErrors.report_name}</div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-auto formicon">
                                <i className="fa-solid fa-calendar-days"></i>
                            </div>
                            <div className="col-sm-5 fromfield">
                                <label htmlFor="from_date">Input Date Range</label>
                                <span>Select the Reporting Period Below </span>
                                <div className="row">
                                    <div className="col">
                                        <div className="form-group">
                                            <input
                                                type="month"
                                                id="from_date"
                                                className="form-control form-control-1 input-sm from date-input"
                                                placeholder="From (Optional)"
                                                value={formData.from_date}
                                                onChange={handleInputChange}
                                                onFocus={handleDateFieldFocus}
                                            />
                                            {validationErrors.from_date && (
                                                <div className="error-message">{validationErrors.from_date}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="form-group">
                                            <input
                                                type="month"
                                                id="to_date"
                                                className="form-control form-control-2 input-sm to date-input"
                                                placeholder="To (Optional)"
                                                value={formData.to_date}
                                                onChange={handleInputChange}
                                                onFocus={handleDateFieldFocus}
                                            />
                                            {validationErrors.to_date && (
                                                <div className="error-message">{validationErrors.to_date}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-auto formicon">
                                <i className="fa-solid fa-file-signature"></i>
                            </div>
                            <div className="col-sm-5 fromfield">
                                <label htmlFor="facility_id">Select Assets</label>
                                <span>Select all the Assets within the reporting boundary.</span>
                                <div className="row">
                                    <div className="col">
                                        <div style={{
                                            border: '3px solid #ddd',
                                            borderRadius: '10px',
                                            padding: '10px',
                                            paddingLeft:'50px',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f9f9f9'
                                        }}>
                                            {data && data.length > 0 ? (
                                                <>
                                                    <div className="form-check" style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="selectAll"
                                                            checked={formData.selectAll}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="selectAll" style={{ fontWeight: 'bold' }}>
                                                            Select All
                                                        </label>
                                                    </div>
                                                    {data.map((curData) => {
                                                        const { id, name } = curData;
                                                        return (
                                                            <div className="form-check" key={id} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={id.toString()}
                                                                    checked={formData.selectAll ? true : formData.facility_id.includes(id)}
                                                                    onChange={handleInputChange}
                                                                />
                                                                <label className="form-check-label" htmlFor={id.toString()}>
                                                                    {name}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                <p>No facilities available.</p>
                                            )}
                                        </div>
                                        {validationErrors.facility_id && (
                                            <div className="error-message">{validationErrors.facility_id}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-6 text-end">
                                <button type="submit" className="btn btn-primary btn-add">
                                    CREATE REPORT
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </main>
        </>
    );
};

export default NewReportForm;